import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'

import { Box, ButtonBase, useTheme } from '@mui/material'

import { CONTROL_HEIGHT } from '@repo/ui'

/**
 * Inner tab height — `CONTROL_HEIGHT`, the page-header band (§29). 4px of track padding sits
 * above and below, so the OUTER track measures 48.
 *
 * ## Why the band token lands on the TAB and not on the track
 *
 * It read as `CONTROL_HEIGHT - 8` — a 40px track around 32px tabs — on the reasoning that the
 * control's outer box should equal a header button's 40. That put the band token on the wrong
 * element, and the result was reported as squat.
 *
 * The thing a reader perceives as "the control" here is the PILL, not the track. The track is
 * chrome — a recessed groove, the same role a card's padding plays — and it is the pill that is
 * pressed, that carries the label, and that sits beside a real button on almost every consumer
 * (Billing's "Add payment method", Signups' "Invite", the CompanyPage tab-row actions). At 32
 * that pill was 8px SHORTER than the 40px button next to it, which is the identical mismatch
 * §29 records for the table toolbar: a 40px toolbar button beside a 49px search field. Measured
 * on /settings/billing before this change: track 40, tab 32, label line-box 19.6.
 *
 * So the tab takes the band height and the track grows to 48 around it.
 *
 * ## Why 48 is safe in the header band, which the previous value existed to protect
 *
 * The comment this replaces argued that a 44px control "pushed the row to 44 and the title to a
 * centre of 46, off the sidebar logo's 44". That was true when written and is no longer: `Header`
 * now pins its title row to `minHeight: 49` precisely so a page's title cannot move as a function
 * of what its header happens to contain. Any `leftComponents` content up to 49px is absorbed by
 * that row without shifting anything.
 *
 * 48 < 49, so the datum holds — verified on /internal/analytics, the case where the tabs sit IN
 * the title row rather than in a `stackControls` row: title centre 44.0 and row height 49.0 both
 * before and after. Note this also makes 48 the CEILING. A future increase past 49 would start
 * stretching that row and would take every page title off the logo line with it.
 */
const TAB_HEIGHT = CONTROL_HEIGHT

export type SegmentedTab = {
  label: string
  value: string
}

interface SegmentedTabsProps {
  tabs: SegmentedTab[]
  value: string
  onChange: (value: string) => void
  /** Stretch the track to fill its container; tabs split the width evenly. */
  fullWidth?: boolean
  /**
   * VRG-247: optional trailing content rendered right-aligned on the SAME
   * horizontal line as the tab track (e.g. page actions that only apply to
   * the active tab). Most consumers route tabs + actions through `Header`'s
   * `leftComponents`/`rightComponents` (stackControls), but pages that render
   * `SegmentedTabs` directly in page content (not via `Header`) had no way to
   * put actions on the tab row itself — this closes that gap on the shared
   * component rather than a one-off flex wrapper per page.
   */
  rightComponents?: React.ReactNode
  /**
   * Tighter horizontal tab padding, for a track that has to live in a narrow column.
   *
   * Added for the docs Internal/External toggle, which sits in the docs left nav — measured at
   * 163px wide at 1280x800. At the default 16px padding an "Internal"/"External" pair needs
   * 184px, so `fullWidth` would have squeezed each label's content box to 43.5px for 54px of
   * text and the label would have overflowed its button. That is precisely the defect VRG-402
   * fixed one component over in `SideNav`, so reproducing it here to make the toggle fit would
   * have been an odd trade.
   *
   * A prop on the shared control rather than an `sx` override at the call site: the padding is
   * load-bearing for whether a label fits, and a one-off override is invisible to the next
   * narrow-column consumer (§47 — improve the base rather than work around it).
   */
  compact?: boolean
  /**
   * The tab the pill should slide FROM on this control's FIRST render, when the control is
   * being remounted as the continuation of a control that was showing a different tab.
   *
   * ## Why a shared control needs to know about its own predecessor
   *
   * The pill slides because `left`/`width` change on ONE element that stays mounted. That
   * holds for every consumer whose tabs are in-page state (Billing, People, the company
   * detail tabs) and it is why they animate with no help from this prop.
   *
   * The docs Internal/External toggle is different in kind: its two states are two ROUTES
   * (`/docs` and `/internal/docs`), rendered by two different route elements. Clicking it
   * unmounts this component and mounts a new one, so the pill that should have travelled is
   * destroyed and a different element is created already at its destination. Measured on the
   * live page before this prop existed: after the switch the pill element had no `data-probe`
   * attribute the previous one carried (a new node), and `transitionrun` fired ZERO times.
   *
   * Nothing is wrong with the transition — it cannot run, because there is no continuous
   * element for it to run on.
   *
   * ## What this prop does, and what it deliberately does not do
   *
   * It hands the new instance the one fact the old instance took with it: where the pill was.
   * The entrance then uses the SAME CSS transition below — it is not a second animation path
   * with its own timing, it just gives the browser a start position to interpolate from. So a
   * change to the duration or curve still governs both, which is why this is a prop here
   * rather than a bespoke animation at the docs call site.
   *
   * Mount-only, and one-shot: it is read on the first layout pass and never again, so a value
   * that lingers in the caller cannot re-trigger it.
   */
  enterFrom?: string
}

type Rect = { left: number; width: number }

/** Two rects are the same position — used to avoid re-rendering on a no-op re-measure. */
const sameRect = (a: Rect | null, b: Rect) => !!a && a.left === b.left && a.width === b.width

/**
 * Segmented "pill" tab control (Vera Design System — internal company/detail pages).
 * A light grey track with a single white, softly-shadowed pill that **slides** to
 * the active tab (animated left/width, easeInOutCubic) rather than flashing in place.
 *
 * The pill geometry is read from the live DOM (the active tab button, by index)
 * inside a layout effect + rAF, so it always targets the current selection after
 * the active styles have settled — no off-by-one, no stale state. State-driven.
 */
export const SegmentedTabs = ({ tabs, value, onChange, fullWidth = false, rightComponents, compact = false, enterFrom }: SegmentedTabsProps) => {
  const theme = useTheme()
  const trackRef = useRef<HTMLDivElement>(null)
  const pillRef = useRef<HTMLDivElement>(null)
  const [pill, setPill] = useState<Rect | null>(null)
  /** Where the pill is heading, while an `enterFrom` entrance is mid-flight. Null otherwise. */
  const entranceTarget = useRef<Rect | null>(null)
  /** The first positioning has happened. Guards the mount-only branch from re-running. */
  const placed = useRef(false)
  /** The mount sequence is over; from here the pill's transitions are the real ones. */
  const settled = useRef(false)

  const rectOf = useCallback((v: string): Rect | null => {
    const track = trackRef.current
    if (!track) return null
    const idx = tabs.findIndex((t) => t.value === v)
    const btn = track.querySelectorAll<HTMLElement>('[role="tab"]')[idx]
    return btn ? { left: btn.offsetLeft, width: btn.offsetWidth } : null
  }, [tabs])

  useLayoutEffect(() => {
    const measure = () => {
      const r = rectOf(value)
      if (r) setPill((prev) => (sameRect(prev, r) ? prev : r))
    }

    // See the track's `overflowX` below. When the strip is narrower than its tabs, keep the
    // ACTIVE tab inside the visible part of it. `scrollLeft` on the track only, never
    // `scrollIntoView`, which would also scroll every scrollable ancestor (the page).
    const keepActiveVisible = () => {
      const track = trackRef.current
      const r = rectOf(value)
      if (!track || !r || track.scrollWidth <= track.clientWidth) return
      const pad = 4
      if (r.left - pad < track.scrollLeft) track.scrollLeft = Math.max(0, r.left - pad)
      else if (r.left + r.width + pad > track.scrollLeft + track.clientWidth)
        track.scrollLeft = r.left + r.width + pad - track.clientWidth
    }
    keepActiveVisible()
    // The track's own width changes without a window resize — opening the Ask panel
    // narrows the page by its width (measured 1199 → 840 at 1280 wide) — and that can
    // push the active tab out of view, or (fullWidth) move every tab under the pill.
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            if (!entranceTarget.current) measure()
            keepActiveVisible()
          })
    if (observer && trackRef.current) observer.observe(trackRef.current)

    if (!placed.current) {
      placed.current = true
      const to = rectOf(value)
      // See `enterFrom`. Render at the OLD tab first; the effect below then moves it, which
      // is the change the CSS transition animates.
      const from = enterFrom && enterFrom !== value ? rectOf(enterFrom) : null
      if (from && to) {
        entranceTarget.current = to
        setPill(from)
      } else {
        measure()
      }
    } else if (!entranceTarget.current) {
      measure()
    }

    // Re-measure after the browser has applied the active font-weight reflow. Skipped while an
    // entrance is pending — it would jump the pill straight to its destination and eat the
    // slide.
    const raf = requestAnimationFrame(() => {
      if (!entranceTarget.current) measure()
    })
    window.addEventListener('resize', measure)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', measure)
      observer?.disconnect()
    }
  }, [value, tabs, enterFrom, rectOf])

  /**
   * The mount sequence's second half. Runs only while `settled` is false, so a normal in-page
   * tab click never reaches it — cancelling transitions there would kill the very slide this
   * component exists for.
   *
   * Two jobs, in order:
   *
   * 1. **Drop the first-positioning artifact.** The pill renders at `left: 0; width: 0` before
   *    it has been measured, and the browser recalculates style at that value, so the move to
   *    the measured position starts a transition FROM the track's left edge at zero width.
   *    Measured on the live page: right after a route change the pill reported `left: 0px,
   *    width: 0px` with two running animations. That is a 320ms grow-out-of-the-corner on every
   *    mount of every consumer, and it is an artifact of not knowing where to be yet, not an
   *    intent. Cancelling reverts the element to its specified position immediately.
   *
   * 2. **Run the entrance.** With the artifact cancelled, the element's computed position IS
   *    `enterFrom`'s. Forcing a layout read establishes that as the transition's before-change
   *    style, so the very next commit — the target position — interpolates from the old tab
   *    rather than from wherever the interrupted artifact had reached. Without the forced read,
   *    both writes land inside one task, the browser only ever sees `0 → target`, and the pill
   *    slides in from the corner again.
   */
  useLayoutEffect(() => {
    if (settled.current || !pill) return
    const el = pillRef.current
    if (!el) return

    el.getAnimations().forEach((a) => a.cancel())

    const to = entranceTarget.current
    if (!to) {
      settled.current = true
      return
    }
    entranceTarget.current = null
    // Deliberate synchronous layout read — see (2) above. It is the whole mechanism.
    void el.offsetWidth
    setPill(to)
    settled.current = true
  }, [pill])

  const track = (
    <Box
      ref={trackRef}
      role="tablist"
      sx={{
        position: 'relative',
        display: fullWidth ? 'flex' : 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        p: '4px',
        borderRadius: '10px',
        backgroundColor: theme.palette.grey[100],
        width: fullWidth ? '100%' : 'fit-content',
        // VRG-643: a track with more tabs than room (the eight SEO sections at ~1024, any
        // long set on a phone) used to overflow its container by its full excess — measured
        // 907px of tabs in a 261px column at 390 wide — which pushed the page's scroll
        // container sideways and put the last tabs off-screen. It now never exceeds its
        // container and scrolls its OWN tabs instead. The pill is absolutely positioned
        // inside this box (`position: relative`), so it scrolls with the tabs, and its
        // offsetLeft geometry is unchanged. A track that fits is unaffected: no overflow,
        // no scrollbar. `minWidth: 0` lets it shrink inside a flex row too (the
        // `rightComponents` wrapper), where a flex item's automatic minimum would otherwise
        // hold it at its full content width.
        maxWidth: '100%',
        minWidth: 0,
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollbarWidth: 'thin',
        // Keep a sideways wheel / swipe on the strip from turning into a browser back/forward.
        overscrollBehaviorX: 'contain',
      }}
    >
      {/* Sliding selection pill (single element shared across tabs). */}
      <Box
        ref={pillRef}
        aria-hidden
        sx={{
          position: 'absolute',
          top: '4px',
          height: TAB_HEIGHT,
          left: `${pill?.left ?? 0}px`,
          width: `${pill?.width ?? 0}px`,
          borderRadius: '8px',
          backgroundColor: theme.palette.background.paper,
          boxShadow:
            '0px 1px 2px rgba(16, 24, 40, 0.10), 0px 1px 3px rgba(16, 24, 40, 0.06)',
          opacity: pill ? 1 : 0,
          transition:
            'left 0.32s cubic-bezier(0.65, 0, 0.35, 1), width 0.32s cubic-bezier(0.65, 0, 0.35, 1)',
          // The pill's whole job is TRAVEL, so it is exactly what someone asking for less
          // motion is asking not to see. Suppressed here rather than by branching in JS, so
          // there is one place that decides whether the pill moves: the entrance still writes
          // its start and end positions, the browser simply lands on the end one. The label
          // colour fade is left alone — a cross-fade is not motion.
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      />
      {tabs.map((tab) => {
        const active = tab.value === value
        return (
          <ButtonBase
            key={tab.value}
            role="tab"
            aria-selected={active}
            disableRipple
            onClick={() => onChange(tab.value)}
            sx={{
              position: 'relative',
              zIndex: 1,
              flex: fullWidth ? 1 : 'none',
              px: compact ? '8px' : '16px',
              height: TAB_HEIGHT,
              borderRadius: '8px',
              fontSize: 14,
              fontWeight: active ? 600 : 500,
              color: active ? theme.palette.text.primary : theme.palette.text.secondary,
              backgroundColor: 'transparent',
              transition: 'color 0.2s ease',
              '&:hover': {
                color: theme.palette.text.primary,
              },
            }}
          >
            {tab.label}
          </ButtonBase>
        )
      })}
    </Box>
  )

  // No rightComponents → render the bare track exactly as before (no extra
  // wrapper), so every existing consumer's markup is unchanged.
  if (!rightComponents) return track

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 3,
        width: fullWidth ? '100%' : undefined,
      }}
    >
      {track}
      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {rightComponents}
      </Box>
    </Box>
  )
}
